import React, { useEffect, useMemo, useState } from "react"
import SearchableSelect, { SearchableSelectOption } from "."
import { Department } from "../../../types/userManagement"
import DepartmentIcon from "../../../assets/icons/department-icon.svg";
import { isNilObjectID } from "../../../lib/objectid/objectid";
import { useDepartment, useDepartmentApi } from "../../../api/platform/useDepartments";

type DepartmentSelectParams = {
  departments: Department[]
  clientID?: string | null;
  multiple?: boolean;
  onSelectionChange?: (departments: Department[]) => void;
  disabled?: boolean;
  allowSelectAll?: boolean;
  noClientOnChange?: boolean;
}

function DepartmentSelect({
  clientID,
  multiple,
  onSelectionChange,
  departments = [],
  disabled,
  allowSelectAll = false,
  noClientOnChange,
}: DepartmentSelectParams) {
  const deptApi = useDepartmentApi();
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<SearchableSelectOption<Department>[]>([])
  const [searchStr, setSearchStr] = useState("")

  const fetchDepartments = async () => {
    setLoading(true)
    try {
      const [res, err] = await deptApi.list({ clientID: clientID ?? "", search: searchStr, limit: 200 })
      if (!err && res) {
        setOptions(res.items.map((d) => ({ label: d.name, value: d, key: d.id })))
      }
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchDepartments()
  }, [clientID, searchStr])

  useEffect(() => {
    if (!noClientOnChange) {
      onChange([])
    }
  }, [clientID, noClientOnChange])

  const onChange = (selected: SearchableSelectOption<Department>[]) => {
    if (onSelectionChange) {
      onSelectionChange(selected.map(s => s.value))
    }
  }

  return (
    <SearchableSelect
      icon={DepartmentIcon}
      label={multiple ? "Departments" : "Department"}
      selected={departments.map(d => ({ label: d.name, value: d, key: d.id }))}
      search={true}
      multiple={multiple ? true : false}
      options={options}
      localSearch={false}
      onChange={onChange}
      onSearch={setSearchStr}
      loading={loading}
      disabled={disabled}
      enableSelectAll={allowSelectAll}
    />
  )
}

export default DepartmentSelect


type DepartmentSelectSingleProps = {
  department?: Department,
  onChange?: (selected: Department | undefined) => void,
  disabled?: boolean,
  initialDepartmentID?: string
  clientID?: string
}

export const DepartmentSelectSingle: React.FC<DepartmentSelectSingleProps> = (props) => {
  const { data: initialDepartment } = useDepartment(
    !isNilObjectID(props.initialDepartmentID) ? (props.initialDepartmentID ?? "") : ""
  )

  useEffect(() => {
    if (initialDepartment && props.onChange) {
      props.onChange(initialDepartment)
    }
  }, [initialDepartment])

  const onChange = (departments: Department[]) => {
    if (!props.onChange) return;
    props.onChange(departments.length > 0 ? departments[0] : undefined)
  }

  return (
    <DepartmentSelect
      clientID={props.clientID}
      disabled={props.disabled}
      multiple={false}
      onSelectionChange={onChange}
      departments={props.department ? [props.department] : []}
    />
  )
}


type DepartmentSelect1WayParams = {
  initialDepartments: Department[]
  clientID?: string | null;
  multiple?: boolean;
  onSelectionChange?: (departments: Department[]) => void;
  disabled?: boolean;
  allowSelectAll?: boolean;
  noClientOnChange?: boolean;
}

export function DepartmentSelect1Way({
  clientID,
  multiple,
  onSelectionChange,
  initialDepartments = [],
  disabled,
  allowSelectAll = false,
  noClientOnChange,
}: DepartmentSelect1WayParams) {
  const deptApi = useDepartmentApi();
  const [departments, setDepartments] = useState<Department[]>(initialDepartments)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<SearchableSelectOption<Department>[]>([])
  const [searchStr, setSearchStr] = useState("")

  const fetchDepartments = async () => {
    setLoading(true)
    try {
      const [res, err] = await deptApi.list({ clientID: clientID ?? "", search: searchStr, limit: 200 })
      if (!err && res) {
        setOptions(res.items.map((d) => ({ label: d.name, value: d, key: d.id })))
      }
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchDepartments()
  }, [clientID, searchStr])

  useEffect(() => {
    if (!noClientOnChange) {
      onChange([])
    }
  }, [clientID, noClientOnChange])

  const onChange = (selected: SearchableSelectOption<Department>[]) => {
    setDepartments(selected.map(s => s.value))
  }

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(departments)
    }
  }, [departments])

  const selected = useMemo(() => {
    return departments.map(d => ({ label: d.name, value: d, key: d.id }))
  }, [departments])

  return (
    <SearchableSelect
      icon={DepartmentIcon}
      label={multiple ? "Departments" : "Department"}
      selected={selected}
      search={true}
      multiple={multiple ? true : false}
      options={options}
      localSearch={false}
      onChange={onChange}
      onSearch={setSearchStr}
      loading={loading}
      disabled={disabled}
      enableSelectAll={allowSelectAll}
    />
  )
}
